import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useTranslation } from "react-i18next"
import {
  SupportCapabilityConfigSchema,
  SupportEscalationStrategy,
  SupportHandoffPolicy,
} from "schema"
import z from "zod"
import { ContactRequirementFields } from "./ContactRequirementFields"
import { SupportReplyPolicyFields } from "./SupportReplyPolicyFields"

type SupportConfig = z.infer<typeof SupportCapabilityConfigSchema>

type Props = {
  value: SupportConfig
  onChange: (value: SupportConfig) => void
}

export function SupportFields({ value, onChange }: Props) {
  const { t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const { isEnabled: isNewSupportFlowEnabled } = useNewSupportFlow()

  const asksCustomerForContact = value.handoffPolicy !== SupportHandoffPolicy.ShareContactDetails

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <span className="text-sm text-muted-foreground">
          {t("capabilities.catalog.Support.config.contactHandoff.description")}
        </span>
        <Label htmlFor="handoff-policy-select">
          {t("capabilities.catalog.Support.config.handoffPolicy.label")}
        </Label>
        <Select
          value={value.handoffPolicy}
          onValueChange={(policy: SupportConfig["handoffPolicy"]) =>
            onChange({ ...value, handoffPolicy: policy })
          }
        >
          <SelectTrigger id="handoff-policy-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(SupportHandoffPolicy).map((policy) => (
              <SelectItem key={policy} value={policy}>
                {t(`capabilities.catalog.Support.config.handoffPolicy.options.${policy}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {t(`capabilities.catalog.Support.config.handoffPolicy.hints.${value.handoffPolicy}`, "")}
        </span>
      </div>
      {(developerMode || isNewSupportFlowEnabled) && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="escalation-strategy-select">
            {t("capabilities.catalog.Support.config.escalationStrategy.label")}
          </Label>
          <Select
            value={value.escalationStrategy}
            onValueChange={(strategy: SupportConfig["escalationStrategy"]) =>
              onChange({ ...value, escalationStrategy: strategy })
            }
          >
            <SelectTrigger id="escalation-strategy-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(SupportEscalationStrategy).map((strategy) => (
                <SelectItem key={strategy} value={strategy}>
                  {t(`capabilities.catalog.Support.config.escalationStrategy.options.${strategy}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {t(
              `capabilities.catalog.Support.config.escalationStrategy.hints.${value.escalationStrategy}`,
            )}
          </span>
        </div>
      )}
      {asksCustomerForContact && (
        <div className="rounded-lg border bg-background p-4">
          <ContactRequirementFields
            value={{
              contactChannel: value.contactChannel,
              additionalFields: value.additionalFields,
            }}
            onChange={(contact) => onChange({ ...value, ...contact })}
          />
        </div>
      )}
      {asksCustomerForContact && (
        <div className="rounded-lg border bg-background p-4">
          <SupportReplyPolicyFields value={value} onChange={onChange} />
        </div>
      )}
      <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">
            {t("capabilities.catalog.Support.config.contactDetails.label")}
          </span>
          <span className="text-sm text-muted-foreground">
            {value.handoffPolicy === "ShareContactDetails"
              ? t("capabilities.catalog.Support.config.contactDetails.descriptionShare")
              : t("capabilities.catalog.Support.config.contactDetails.descriptionOptional")}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 flex-1">
            <Label htmlFor="support-phone">
              {t("capabilities.catalog.Support.config.supportPhone.label")}
            </Label>
            <Input
              id="support-phone"
              value={value.supportPhone}
              onChange={(e) => onChange({ ...value, supportPhone: e.target.value })}
              placeholder={t("capabilities.catalog.Support.config.supportPhone.placeholder")}
            />
          </div>
          <div className="flex flex-col gap-1.5 flex-1">
            <Label htmlFor="support-email">
              {t("capabilities.catalog.Support.config.supportEmail.label")}
            </Label>
            <Input
              id="support-email"
              type="email"
              value={value.supportEmail}
              onChange={(e) => onChange({ ...value, supportEmail: e.target.value })}
              placeholder={t("capabilities.catalog.Support.config.supportEmail.placeholder")}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
